<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import { useLanguageStore } from '../../stores/languageStore';
import { Carousel, Slide, Navigation } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import apiService from '../../services/apiService';

const languageStore = useLanguageStore();
const popularVideos = ref<any[]>([]);
const isLoading = ref(true);
const error = ref('');
const regionCode = ref('TR');

// Popüler videoları yükleyen fonksiyon
const fetchPopularVideos = async () => {
  try {
    isLoading.value = true;
    error.value = '';
    
    // API'yi çağır
    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/videos/popular-youtube?regionCode=${regionCode.value}&maxResults=10`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(errorData?.message || `HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.success && Array.isArray(data.videos)) {
      popularVideos.value = data.videos;
    } else {
      throw new Error('Invalid API response format');
    }
  } catch (err) {
    console.error('Error fetching popular videos:', err);
    error.value = err instanceof Error ? err.message : 'An error occurred while fetching videos';
    popularVideos.value = []; // Reset videos on error
  } finally {
    isLoading.value = false;
  }
};

// Video izleme sayfasına yönlendirme fonksiyonu
const handleVideoClick = (videoId: string) => {
  // Video URL'sini oluştur
  const videoUrl = `https://www.youtube.com/watch?v=${videoId}`;
  
  // Yeni sekmede açma işlemi
  window.open(videoUrl, '_blank');
};

// Görüntüleme sayısını formatlayan fonksiyon
const formatViewCount = (viewCount: number): string => {
  if (viewCount >= 1000000) {
    return `${(viewCount / 1000000).toFixed(1)}M`;
  } else if (viewCount >= 1000) {
    return `${(viewCount / 1000).toFixed(1)}K`;
  }
  return viewCount.toString();
};

// Süreyi formatlayan fonksiyon (PT1H20M30S -> 1:20:30)
const formatDuration = (duration: string): string => {
  // PT1H20M30S -> Bu format ISO 8601 duration formatı
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  
  if (!match) {
    return '00:00';
  }
  
  const hours = match[1] ? parseInt(match[1]) : 0;
  const minutes = match[2] ? parseInt(match[2]) : 0;
  const seconds = match[3] ? parseInt(match[3]) : 0;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
};

// Bileşen oluşturulduğunda popüler videoları yükle
onMounted(() => {
  fetchPopularVideos();
});

// Bölge kodunu değiştirmek için kullanılacak fonksiyon
const changeRegion = (code: string) => {
  regionCode.value = code;
  fetchPopularVideos();
};
</script>

<template>
  <div class="py-12 sm:py-16 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="text-center mb-8">
        <h2 class="text-3xl sm:text-4xl font-bold text-gray-900">{{ languageStore.t('home.popularVideos.title') || 'Popüler Videolar' }}</h2>
        <p class="mt-4 text-xl text-gray-600">{{ languageStore.t('home.popularVideos.subtitle') || 'Günün en çok izlenen videoları' }}</p>
        
        <!-- Bölge değiştirme butonları -->
        <div class="mt-6 flex flex-wrap items-center justify-center gap-2">
          <button 
            @click="changeRegion('TR')" 
            :class="['px-3 py-1.5 text-sm font-medium rounded-full transition-colors', 
                    regionCode === 'TR' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
          >
            {{ languageStore.t('home.popularVideos.regions.Turkey') || 'Türkiye' }}
          </button>
          <button 
            @click="changeRegion('US')" 
            :class="['px-3 py-1.5 text-sm font-medium rounded-full transition-colors', 
                    regionCode === 'US' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
          >
            {{ languageStore.t('home.popularVideos.regions.US') || 'ABD' }}
          </button>
          <button 
            @click="changeRegion('GB')" 
            :class="['px-3 py-1.5 text-sm font-medium rounded-full transition-colors', 
                    regionCode === 'GB' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
          >
            {{ languageStore.t('home.popularVideos.regions.UK') || 'İngiltere' }}
          </button>
          <button 
            @click="changeRegion('DE')" 
            :class="['px-3 py-1.5 text-sm font-medium rounded-full transition-colors', 
                    regionCode === 'DE' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
          >
            {{ languageStore.t('home.popularVideos.regions.Germany') || 'Almanya' }}
          </button>
          <button 
            @click="changeRegion('FR')" 
            :class="['px-3 py-1.5 text-sm font-medium rounded-full transition-colors', 
                    regionCode === 'FR' ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200']"
          >
            {{ languageStore.t('home.popularVideos.regions.France') || 'Fransa' }}
          </button>
        </div>
      </div>
      
      <!-- Loading state -->
      <div v-if="isLoading" class="flex justify-center items-center py-16">
        <div class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600"></div>
      </div>
      
      <!-- Error state -->
      <div v-else-if="error" class="text-center py-10">
        <div class="text-red-500 mb-3">{{ error }}</div>
        <button 
          @click="fetchPopularVideos" 
          class="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
        >
          {{ languageStore.t('home.popularVideos.tryAgain') || 'Yeniden Dene' }}
        </button>
      </div>
      
      <!-- Carousel -->
      <div v-else-if="popularVideos.length > 0" class="mt-6">
        <Carousel
          :items-to-show="1.5"
          :breakpoints="{
            480: { itemsToShow: 1.8 },
            640: { itemsToShow: 2.2 },
            768: { itemsToShow: 3 },
            1024: { itemsToShow: 4 }
          }"
          :wrap-around="true"
          :transition="500"
          :snap-align="'center'"
          :mouseDrag="true"
          :touchDrag="true"
        >
          <Slide 
            v-for="video in popularVideos" 
            :key="video.id"
            class="cursor-pointer"
            @click="handleVideoClick(video.id)"
          >
            <div class="carousel__item bg-white rounded-lg overflow-hidden shadow-md mx-2 my-2 transform transition-transform hover:scale-[1.03] hover:shadow-lg">
              <!-- Thumbnail -->
              <div class="relative aspect-video overflow-hidden">
                <img 
                  :src="video.thumbnail_url"
                  :alt="video.title"
                  class="w-full h-full object-cover"
                  loading="lazy"
                />
                <!-- Duration badge -->
                <div class="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/80 text-white text-xs font-bold rounded">
                  {{ formatDuration(video.duration) }}
                </div>
              </div>
              
              <!-- Video info -->
              <div class="p-3">
                <h3 class="text-sm font-medium text-gray-900 line-clamp-2 mb-1">{{ video.title }}</h3>
                <div class="flex justify-between items-center text-xs text-gray-600">
                  <div>{{ formatViewCount(video.viewCount) }} {{ languageStore.t('home.popularVideos.viewCount') || 'görüntüleme' }}</div>
                  <div class="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    {{ formatViewCount(video.likeCount) }}
                  </div>
                </div>
              </div>
            </div>
          </Slide>
          
          <template #addons>
            <Navigation />
          </template>
        </Carousel>
      </div>
      
      <!-- No videos state -->
      <div v-else class="text-center py-10">
        <p class="text-gray-500">{{ languageStore.t('home.popularVideos.noVideos') || 'Popüler video bulunamadı.' }}</p>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Carousel custom styling */
:deep(.carousel__slide) {
  padding: 5px;
}

:deep(.carousel__viewport) {
  perspective: 1000px;
}

:deep(.carousel__track) {
  transform-style: preserve-3d;
}

:deep(.carousel__slide--sliding) {
  transition: 0.3s;
}

:deep(.carousel__slide) {
  opacity: 0.95;
  transform: rotateY(-2deg) scale(0.97);
}

:deep(.carousel__slide--active ~ .carousel__slide) {
  transform: rotateY(2deg) scale(0.97);
}

:deep(.carousel__slide--prev) {
  opacity: 1;
  transform: rotateY(-1deg) scale(0.99);
}

:deep(.carousel__slide--next) {
  opacity: 1;
  transform: rotateY(1deg) scale(0.99);
}

:deep(.carousel__slide--active) {
  opacity: 1;
  transform: rotateY(0) scale(1.02);
}

/* Navigation buttons styling */
:deep(.carousel__prev),
:deep(.carousel__next) {
  background-color: rgba(79, 70, 229, 0.8);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

:deep(.carousel__prev:hover),
:deep(.carousel__next:hover) {
  background-color: rgba(79, 70, 229, 1);
  transform: scale(1.1);
}

/* Responsive adjustments */
@media (max-width: 640px) {
  :deep(.carousel__prev),
  :deep(.carousel__next) {
    width: 32px;
    height: 32px;
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;  
  overflow: hidden;
}
</style> 